import type { LocationQueryRaw, RouteLocationNormalizedLoaded, RouteLocationRaw, Router } from 'vue-router'
import type { LinkedRateCombo } from '../../utils/linked-rate-matcher'

export type CombinationTrunkType = 'fcl' | 'lcl' | 'air'
type CombinationSegment = 'trunk' | 'preport' | 'lastmile'

const detailRouteNames = {
  fcl: 'SaasRateWarehouseFclDetail',
  lcl: 'SaasRateWarehouseLclDetail',
  air: 'SaasRateWarehouseAirDetail',
  preport: 'SaasRateWarehousePreportDetail',
  lastmile: 'SaasRateWarehouseLastmileDetail'
} as const

/** 将当前卡片匹配的运价 ID 随详情路由传递，切换和刷新时仍保留同一组组合。 */
export const buildCombinationDetailQuery = (
  combo: LinkedRateCombo,
  trunkType: CombinationTrunkType,
  returnTo: string
): LocationQueryRaw => ({
  returnTo,
  combinationTrunkType: trunkType,
  combinationTrunkId: String(combo.trunkRate.id),
  combinationPreportId: combo.preportRate?.id ? String(combo.preportRate.id) : undefined,
  combinationLastmileId: combo.lastmileRate?.id ? String(combo.lastmileRate.id) : undefined
})

/** 仅接受单值字符串参数，避免重复参数或空值被用作运价 ID。 */
const readQueryString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined

/** 只有从组合查询进入的只读匹配详情才启用 tab，其他详情入口保持原有行为。 */
export const resolveCombinationDetailContext = (route: RouteLocationNormalizedLoaded, router: Router) => {
  const trunkType = readQueryString(route.query.combinationTrunkType)
  const trunkId = readQueryString(route.query.combinationTrunkId)
  const returnTo = readQueryString(route.query.returnTo)
  if (route.params.mode !== 'view' || !trunkId || !returnTo || !returnTo.startsWith('/')
    || (trunkType !== 'fcl' && trunkType !== 'lcl' && trunkType !== 'air')) return null

  const returnRouteName = router.resolve(returnTo).name
  if (returnRouteName !== 'ShokzRateQueryCombination' && returnRouteName !== 'SaasRateQueryCombination') return null

  const segmentIds = {
    trunk: trunkId,
    preport: readQueryString(route.query.combinationPreportId),
    lastmile: readQueryString(route.query.combinationLastmileId)
  }
  const segmentRoutes = {
    trunk: detailRouteNames[trunkType],
    preport: detailRouteNames.preport,
    lastmile: detailRouteNames.lastmile
  }
  const activeSegment = (Object.keys(segmentRoutes) as CombinationSegment[])
    .find(segment => route.name === segmentRoutes[segment])
  if (!activeSegment || String(route.params.id) !== segmentIds[activeSegment]) return null

  return { activeSegment, segmentIds, segmentRoutes, returnTo }
}

/** 根据当前组合切换已匹配分段，缺失分段不产生跳转。 */
export const buildCombinationSegmentLocation = (
  route: RouteLocationNormalizedLoaded,
  router: Router,
  segment: string | number
): RouteLocationRaw | null => {
  const context = resolveCombinationDetailContext(route, router)
  if (!context || (segment !== 'trunk' && segment !== 'preport' && segment !== 'lastmile')) return null
  const id = context.segmentIds[segment]
  if (!id || segment === context.activeSegment) return null

  return {
    name: context.segmentRoutes[segment],
    params: { mode: 'view', id },
    query: route.query
  }
}
